import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

export interface MultiSelectOption {
    value: string;
    label: string;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    emptyText?: string;
    className?: string;
    hasError?: boolean;
}

export function MultiSelect({
    options,
    value,
    onChange,
    placeholder = "Select items...",
    disabled = false,
    isLoading = false,
    loadingText = "Loading...",
    emptyText = "No items available",
    className,
    hasError = false,
}: MultiSelectProps) {
    const [open, setOpen] = React.useState(false);

    const selectedOptions = options.filter((opt) => value.includes(opt.value));

    const handleToggle = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter((v) => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const handleRemove = (optionValue: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(value.filter((v) => v !== optionValue));
    };

    const handleClearAll = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onChange([]);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild disabled={disabled || isLoading}>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-full justify-between min-h-10 h-auto py-2 px-3 font-normal",
                        "rounded-md border border-slate-700 bg-slate-950/50 px-3 py-2 text-sm text-white ring-offset-slate-950 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
                        "hover:bg-slate-950/50 hover:text-white hover:border-slate-700",
                        hasError && "border-red-500 focus:ring-red-500",
                        isLoading && "!cursor-not-allowed !pointer-events-auto",
                        open && "cursor-default",
                        className
                    )}
                >
                    {isLoading ? (
                        <span className="flex items-center gap-2 text-slate-400">
                            {loadingText}
                        </span>
                    ) : selectedOptions.length > 0 ? (
                        <div className="flex flex-wrap gap-1 flex-1">
                            {selectedOptions.map((opt) => (
                                <Badge
                                    key={opt.value}
                                    variant="secondary"
                                    className="bg-slate-800 text-slate-200 hover:bg-slate-700 gap-1"
                                >
                                    {opt.label}
                                    <span
                                        role="button"
                                        onMouseDown={(e) => handleRemove(opt.value, e)}
                                        className="inline-flex"
                                    >
                                        <X className="w-3 h-3 cursor-pointer hover:text-red-400" />
                                    </span>
                                </Badge>
                            ))}
                        </div>
                    ) : (
                        <span className="text-white">{placeholder}</span>
                    )}
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                        {selectedOptions.length > 0 && (
                            <span
                                role="button"
                                onMouseDown={handleClearAll}
                                className="inline-flex"
                            >
                                <X className="w-4 h-4 text-slate-400 hover:text-white cursor-pointer" />
                            </span>
                        )}
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent 
                className="w-[var(--radix-popover-trigger-width)] p-0 bg-slate-900 border-slate-600"
                align="start"
            >
                <div className="max-h-60 overflow-auto p-1">
                    {options.length === 0 ? (
                        <div className="py-1.5 px-3 text-white text-sm opacity-50">
                            {emptyText}
                        </div>
                    ) : (
                        options.map((option) => {
                            const isSelected = value.includes(option.value);
                            return (
                                <div
                                    key={option.value}
                                    className={cn(
                                        "flex items-center gap-2 my-1 px-3 py-2 rounded-md cursor-pointer",
                                        "hover:bg-slate-800 transition-colors",
                                        isSelected && "bg-slate-700/50"
                                    )}
                                    onClick={() => handleToggle(option.value)}
                                >
                                    <span className="text-sm text-slate-200">{option.label}</span>
                                    {isSelected && (
                                        <Check className="w-4 h-4 text-emerald-500 ml-auto" />
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
